import { motion } from "framer-motion";
import { ReactNode } from "react";

interface IconCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  className?: string;
  iconClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  delay?: number;
  animation?: {
    initial?: any;
    whileInView?: any;
    transition?: any;
  };
}

const IconCard = ({
  icon,
  title,
  description,
  className = "",
  iconClassName = "",
  titleClassName = "",
  descriptionClassName = "",
  delay = 0,
  animation = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay }
  }
}: IconCardProps) => {
  return (
    <motion.div
      className={`text-center group cursor-pointer relative ${className}`}
      initial={animation.initial}
      whileInView={animation.whileInView}
      transition={animation.transition}
      viewport={{ once: true }}
      whileHover={{ 
        y: -8,
        scale: 1.02,
        transition: { duration: 0.3, ease: "easeOut" }
      }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Animated background glow effect - contained within the card */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-teal-400/20 to-blue-500/20 rounded-2xl opacity-0 group-hover:opacity-100 blur-xl -z-10"
        transition={{ duration: 0.3 }}
      />
      
      {/* Main content container */}
      <div className="relative z-10 h-full p-6 rounded-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 group-hover:border-teal-400/30 transition-all duration-300 flex flex-col">
        {/* Icon container with enhanced animations */}
        <motion.div 
          className={`w-16 h-16 bg-gradient-to-br from-teal-400/10 to-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:from-teal-400/20 group-hover:to-blue-500/20 transition-all duration-300 flex-shrink-0 ${iconClassName}`}
          whileHover={{
            scale: 1.1,
            rotate: [0, -5, 5, -5, 0],
            transition: { duration: 0.6, ease: "easeInOut" }
          }}
        >
          <motion.div 
            className="text-teal-400 group-hover:text-teal-300 transition-colors duration-300"
            whileHover={{
              scale: 1.2,
              transition: { duration: 0.2 }
            }}
          >
            {icon}
          </motion.div>
        </motion.div>
        
        {/* Content area that grows to fill available space */}
        <div className="flex flex-col flex-grow">
          {/* Title with enhanced hover effects */}
          <motion.h3 
            className={`text-xl font-bold mb-3 group-hover:text-teal-400 transition-colors duration-300 flex-shrink-0 ${titleClassName}`}
            whileHover={{
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
          >
            {title}
          </motion.h3>
          
          {/* Description with subtle animation - grows to fill remaining space */}
          <motion.p 
            className={`text-slate-400 group-hover:text-slate-300 transition-colors duration-300 flex-grow flex items-start ${descriptionClassName}`}
            whileHover={{
              y: -2,
              transition: { duration: 0.2 }
            }}
          >
            {description}
          </motion.p>
        </div>
        
        {/* Decorative elements that appear on hover */}
        <motion.div
          className="absolute top-4 right-4 w-2 h-2 bg-teal-400 rounded-full opacity-0 group-hover:opacity-100"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0, 1, 0]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: 0.5
          }}
        />
        <motion.div
          className="absolute bottom-4 left-4 w-1 h-1 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0, 1, 0]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: 1
          }}
        />
      </div>
    </motion.div>
  );
};

export default IconCard;
